/**
 * Backend recovery-requests API — typed TanStack Query hooks.
 *
 * Surface:
 *   useAdminRecoveryRequests(status?)    — super_admin cross-tenant queue
 *   useApproveRecoveryRequest()          — super_admin + step-up
 *   useRejectRecoveryRequest()           — super_admin + step-up (notes mandatory;
 *                                          also valid on stranded 'approved' rows)
 *   useReissueRecoveryRequest()          — super_admin + step-up; re-fires a
 *                                          stranded 'approved' row (the executor
 *                                          consumes change-feed events even on
 *                                          failure, so a failed row never retries
 *                                          on its own — re-issue touches the doc
 *                                          which re-fires the feed)
 *   useCreateRecoveryRequest()           — anonymous pre-auth submit (lost-both)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';

export type RecoveryTier = 1 | 2 | 3;
export type RecoveryStatus =
  | 'pending'
  | 'in-review'
  | 'approved'
  | 'rejected'
  | 'executed';

export interface RecoveryRequestView {
  id: string;
  tenantId: string;
  subjectUserId: string;
  submittedByUserId: string;
  email?: string;
  tier: RecoveryTier;
  reason: string;
  kbaAnswers?: Record<string, string>;
  evidenceDocumentIds: string[];
  status: RecoveryStatus;
  reviewerUserId?: string;
  reviewerNotes?: string;
  decidedAt?: string;
  executedAt?: string;
  /** Failed executor runs since approval / last re-issue (bounded retry). */
  executeAttempts?: number;
  /** Last executor error — surfaced on stranded 'approved' rows. */
  lastExecuteError?: string;
  reissuedAt?: string;
  reissueCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminRecoveryListResponse {
  recoveryRequests: RecoveryRequestView[];
  correlationId: string;
}

export interface AdminRecoveryDetailResponse {
  recoveryRequest: RecoveryRequestView;
  correlationId: string;
}

export interface UseAdminRecoveryRequestsOptions {
  status?: RecoveryStatus;
  limit?: number;
}

export function useAdminRecoveryRequests(
  opts: UseAdminRecoveryRequestsOptions & { enabled?: boolean } = {},
) {
  // The backend GET /v1/admin/recovery-requests is super_admin only. Tenant
  // admins are 403'd. Default `enabled` to false so the hook is a no-op for
  // callers that don't know the role yet — the admin-layout sidebar gates
  // this on `role === 'super_admin'` to avoid a 403 on every admin page
  // load (one console-error per page navigation, regression noticed in
  // 2026-05-19 e2e walk).
  const { enabled = true, ...rest } = opts;
  return useQuery<AdminRecoveryListResponse>({
    queryKey: ['admin-recovery-requests', 'list', rest.status ?? null, rest.limit ?? null],
    enabled,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (rest.status) params.set('status', rest.status);
      if (rest.limit) params.set('limit', String(rest.limit));
      const q = params.toString();
      const url = q
        ? `/api/v1/admin/recovery-requests?${q}`
        : '/api/v1/admin/recovery-requests';
      return api<AdminRecoveryListResponse>(url, { signal });
    },
  });
}

export interface ApproveRecoveryRequest {
  id: string;
  tenantId: string;
  reviewerNotes?: string;
}

export function useApproveRecoveryRequest() {
  const qc = useQueryClient();
  return useMutation<AdminRecoveryDetailResponse, ApiError, ApproveRecoveryRequest>({
    mutationFn: ({ id, ...body }) =>
      api<AdminRecoveryDetailResponse>(
        `/api/v1/admin/recovery-requests/${encodeURIComponent(id)}/approve`,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-recovery-requests'] });
    },
  });
}

export interface RejectRecoveryRequest {
  id: string;
  tenantId: string;
  reviewerNotes: string; // mandatory at the schema layer
}

export function useRejectRecoveryRequest() {
  const qc = useQueryClient();
  return useMutation<AdminRecoveryDetailResponse, ApiError, RejectRecoveryRequest>({
    mutationFn: ({ id, ...body }) =>
      api<AdminRecoveryDetailResponse>(
        `/api/v1/admin/recovery-requests/${encodeURIComponent(id)}/reject`,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-recovery-requests'] });
    },
  });
}

export interface ReissueRecoveryRequest {
  id: string;
  tenantId: string;
  reviewerNotes?: string;
}

export function useReissueRecoveryRequest() {
  const qc = useQueryClient();
  return useMutation<AdminRecoveryDetailResponse, ApiError, ReissueRecoveryRequest>({
    mutationFn: ({ id, ...body }) =>
      api<AdminRecoveryDetailResponse>(
        `/api/v1/admin/recovery-requests/${encodeURIComponent(id)}/reissue`,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-recovery-requests'] });
    },
  });
}

// ── Anonymous pre-auth submit (lost-both) ─────────────────────────────────────

export interface CreateRecoveryRequest {
  email: string;
  tier: RecoveryTier;
  reason?: string;
  kbaAnswers?: Record<string, string>;
  evidenceDocumentIds?: string[];
}

export interface CreateRecoveryResponse {
  recoveryRequestId: string;
  tier: RecoveryTier;
  expectedResponseTime: 'within-24-hours' | 'within-72-hours' | 'within-10-business-days';
  correlationId: string;
}

export function useCreateRecoveryRequest() {
  return useMutation<CreateRecoveryResponse, ApiError, CreateRecoveryRequest>({
    mutationFn: (body) =>
      api<CreateRecoveryResponse>('/api/v1/auth/recovery', {
        method: 'POST',
        body,
      }),
  });
}
